import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import {
  Loader2,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Play,
  Ban,
  RefreshCw,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export function CleanupDuplicatesCard() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const queryClient = useQueryClient()

  // Poll cleanup job status from server (persists across F5)
  const { data: job, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['cleanup-status'],
    queryFn: () => api.members.getCleanupStatus(),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'pending' || status === 'processing') return 2000
      return false
    },
  })

  const isJobActive = job?.status === 'pending' || job?.status === 'processing'

  // Cancel active job mutation
  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => api.members.cancelCleanup(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleanup-status'] })
      toast.success('Job cancelado com sucesso')
    },
    onError: (error: Error) => {
      toast.error('Erro ao cancelar job', { description: error.message })
    },
  })

  // Resync Klaviyo status polling
  const { data: resyncJob } = useQuery({
    queryKey: ['resync-klaviyo-status'],
    queryFn: () => api.members.getResyncStatus(),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'pending' || status === 'processing') return 2000
      return false
    },
  })

  const isResyncActive = resyncJob?.status === 'pending' || resyncJob?.status === 'processing'

  const resyncMutation = useMutation({
    mutationFn: () => api.members.startResyncKlaviyo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resync-klaviyo-status'] })
      toast.info('Resync Klaviyo iniciado...')
    },
    onError: (error: Error) => {
      toast.error('Erro ao iniciar resync', { description: error.message })
    },
  })

  const resyncProgress =
    resyncJob && (resyncJob.totalGroups ?? 0) > 0
      ? Math.min(Math.round(((resyncJob.processedGroups ?? 0) / resyncJob.totalGroups) * 100), 100)
      : 0

  // Start dry-run mutation
  const dryRunMutation = useMutation({
    mutationFn: () => api.members.startCleanup(true),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cleanup-status'] })
      if (data.totalGroups === 0) {
        toast.success('Nenhuma duplicata encontrada!')
      } else {
        toast.info('Verificação iniciada...')
      }
    },
    onError: (error: Error) => {
      toast.error('Erro ao iniciar verificação', { description: error.message })
    },
  })

  // Start execute mutation
  const executeMutation = useMutation({
    mutationFn: () => api.members.startCleanup(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cleanup-status'] })
      toast.info('Limpeza iniciada...')
    },
    onError: (error: Error) => {
      toast.error('Erro ao iniciar limpeza', { description: error.message })
    },
  })

  const handleDryRun = () => {
    dryRunMutation.mutate()
  }

  const handleExecute = () => {
    setShowConfirmDialog(false)
    executeMutation.mutate()
  }

  const progress =
    job && job.totalGroups > 0
      ? Math.min(Math.round(((job.processedGroups ?? 0) / job.totalGroups) * 100), 100)
      : 0

  const isButtonLoading = dryRunMutation.isPending || executeMutation.isPending

  return (
    <Card className="border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/10">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-9 w-9 shrink-0 rounded-lg bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-base text-orange-900 dark:text-orange-100">
              Manutenção de Base
            </CardTitle>
            <CardDescription className="text-orange-700/80 dark:text-orange-300/80 text-xs mt-0.5">
              Identifique e remova perfis duplicados criados por importações antigas.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active job — progress bar */}
        {isJobActive && job && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-800 dark:text-blue-200">
              <Loader2 className="h-4 w-4 animate-spin" />
              {job.dryRun ? 'Verificando duplicatas...' : 'Executando limpeza...'}
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {job.processedGroups ?? 0} de {job.totalGroups} grupos processados
              </span>
              <span>{progress}%</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
              onClick={() => cancelMutation.mutate(job.id)}
              loading={cancelMutation.isPending}
            >
              <Ban className="mr-1 h-3 w-3" />
              Cancelar
            </Button>
            {(job.membersDeleted ?? 0) > 0 && (
              <div className="text-xs text-muted-foreground">
                {job.dryRun ? 'A remover' : 'Removidos'}:{' '}
                <strong className="text-red-600 dark:text-red-400">{job.membersDeleted}</strong>{' '}
                perfis
              </div>
            )}
          </div>
        )}

        {/* Completed/Failed job — results summary */}
        {!isJobActive && job && (job.status === 'completed' || job.status === 'failed') && (
          <div
            className={`rounded-lg border p-4 space-y-2 ${
              job.status === 'failed' || (job.errors?.length ?? 0) > 0
                ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/30'
                : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
            }`}
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              {job.status === 'failed' || (job.errors?.length ?? 0) > 0 ? (
                <>
                  <XCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-yellow-800 dark:text-yellow-200">
                    {job.status === 'failed'
                      ? 'Falhou'
                      : `Concluído com ${job.errors?.length ?? 0} erro(s)`}
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-800 dark:text-green-200">
                    {job.dryRun ? 'Verificação concluída' : 'Limpeza concluída'}
                  </span>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <span className="block font-bold">{job.totalGroups}</span>
                <span className="text-muted-foreground">grupos</span>
              </div>
              <div>
                <span className="block font-bold">{job.membersAnalyzed}</span>
                <span className="text-muted-foreground">analisados</span>
              </div>
              <div>
                <span className="block font-bold text-red-600 dark:text-red-400">
                  {job.membersDeleted}
                </span>
                <span className="text-muted-foreground">
                  {job.dryRun ? 'a remover' : 'removidos'}
                </span>
              </div>
              <div>
                <span className="block font-bold text-green-600 dark:text-green-400">
                  {job.membersKept}
                </span>
                <span className="text-muted-foreground">mantidos</span>
              </div>
            </div>

            {(job.errors?.length ?? 0) > 0 && (
              <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                <strong>Erro:</strong> {job.errors?.[0]}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {!isJobActive && (
          <div className="flex flex-wrap gap-2">
            {/* After a dry-run completes with duplicates found, show execute button */}
            {job?.status === 'completed' && job.dryRun && job.totalGroups > 0 ? (
              <>
                <Button
                  variant="outline"
                  className="border-orange-200 hover:bg-orange-100 text-orange-700 hover:text-orange-800 dark:border-orange-800 dark:hover:bg-orange-900/50 dark:text-orange-300"
                  onClick={handleDryRun}
                  disabled={executeMutation.isPending}
                  loading={dryRunMutation.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Verificar Novamente
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={dryRunMutation.isPending || isResyncActive}
                  loading={executeMutation.isPending}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Executar Limpeza ({job.membersDeleted} perfis)
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                className="w-full sm:w-auto border-orange-200 hover:bg-orange-100 text-orange-700 hover:text-orange-800 dark:border-orange-800 dark:hover:bg-orange-900/50 dark:text-orange-300"
                onClick={handleDryRun}
                disabled={isLoadingStatus || isResyncActive}
                loading={isButtonLoading}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Verificar Duplicatas
              </Button>
            )}
          </div>
        )}

        {/* Confirmation dialog before executing cleanup */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Confirmar Limpeza
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4 pt-2">
                <div className="text-sm">
                  A verificação encontrou duplicatas. Confira o que será feito:
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-muted p-3 rounded-md">
                    <span className="block text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">
                      Encontrados
                    </span>
                    <span className="text-lg font-bold">{job?.totalGroups ?? 0}</span>
                    <span className="text-xs text-muted-foreground ml-1">grupos duplicados</span>
                  </div>
                  <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-md border border-red-100 dark:border-red-900">
                    <span className="block text-xs text-red-600 dark:text-red-400 uppercase tracking-wider font-semibold mb-1">
                      Serão Removidos
                    </span>
                    <span className="text-lg font-bold text-red-700 dark:text-red-300">
                      {job?.membersDeleted ?? 0}
                    </span>
                    <span className="text-xs text-red-600/80 dark:text-red-400/80 ml-1">
                      perfis vazios
                    </span>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-md border border-green-100 dark:border-green-900">
                    <span className="block text-xs text-green-600 dark:text-green-400 uppercase tracking-wider font-semibold mb-1">
                      Serão Mantidos
                    </span>
                    <span className="text-lg font-bold text-green-700 dark:text-green-300">
                      {job?.membersKept ?? 0}
                    </span>
                    <span className="text-xs text-green-600/80 dark:text-green-400/80 ml-1">
                      perfis oficiais
                    </span>
                  </div>
                  {(job?.manualMergeRequired ?? 0) > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-950/30 p-3 rounded-md border border-yellow-100 dark:border-yellow-900">
                      <span className="block text-xs text-yellow-600 dark:text-yellow-400 uppercase tracking-wider font-semibold mb-1">
                        Revisão Manual
                      </span>
                      <span className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                        {job?.manualMergeRequired}
                      </span>
                      <span className="text-xs text-yellow-600/80 dark:text-yellow-400/80 ml-1">
                        grupos ignorados (conflito)
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md space-y-1">
                  <p>
                    <strong>Segurança:</strong> Apenas perfis sem histórico (transações/indicações)
                    serão removidos.
                  </p>
                  <p>
                    <strong>Sincronização:</strong> O perfil mantido terá seu código de indicação
                    ressincronizado com o Klaviyo.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleExecute}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Executar Limpeza
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Resync Klaviyo section */}
        <div className="border-t border-orange-200 dark:border-orange-900 pt-4 mt-2">
          <h4 className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">
            Resync Klaviyo
          </h4>
          <p className="text-xs text-muted-foreground mb-3">
            Re-sincroniza todos os perfis de membros ativos no Klaviyo com os códigos de indicação
            corretos.
          </p>

          {isResyncActive && resyncJob && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 space-y-3 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-800 dark:text-blue-200">
                <Loader2 className="h-4 w-4 animate-spin" />
                Sincronizando perfis com Klaviyo...
              </div>
              <Progress value={resyncProgress} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {resyncJob.processedGroups ?? 0} de {resyncJob.totalGroups} membros processados
                </span>
                <span>{resyncProgress}%</span>
              </div>
            </div>
          )}

          {!isResyncActive && resyncJob?.status === 'completed' && (
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-3 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-green-800 dark:text-green-200">
                <CheckCircle2 className="h-4 w-4" />
                Resync concluído — {resyncJob.processedGroups} perfis sincronizados
              </div>
            </div>
          )}

          {!isResyncActive && resyncJob?.status === 'failed' && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
                <XCircle className="h-4 w-4" />
                Resync falhou
              </div>
            </div>
          )}

          {!isResyncActive && (
            <Button
              variant="outline"
              size="sm"
              className="border-orange-200 hover:bg-orange-100 text-orange-700 hover:text-orange-800 dark:border-orange-800 dark:hover:bg-orange-900/50 dark:text-orange-300"
              onClick={() => resyncMutation.mutate()}
              disabled={isJobActive}
              loading={resyncMutation.isPending}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {resyncJob?.status === 'completed' ? 'Executar Novamente' : 'Iniciar Resync'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
